import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

type EnhancedAllCampaignsResponse =
  components["schemas"]["EnhancedAllCampaignsResponse"];

type ApiResponseEnhancedAllCampaignsResponse =
  components["schemas"]["ApiResponse_EnhancedAllCampaignsResponse_"];
// Data types from the API schema
type AggregatedMetricsWithSummary =
  components["schemas"]["AggregatedMetricsWithSummary"];
type CampaignMetricsBreakdown =
  components["schemas"]["CampaignMetricsBreakdown"];

// Response types
type ApiResponseAggregatedMetricsWithSummary =
  components["schemas"]["ApiResponse_AggregatedMetricsWithSummary_"];
type ApiResponseCampaignMetricsBreakdown =
  components["schemas"]["ApiResponse_CampaignMetricsBreakdown_"];

// Parameter types from operations
type GetAllCampaignsMetricsParams =
  operations["GetAllCampaignsMetrics"]["parameters"]["query"];
type GetCampaignMetricsWithBreakdownParams =
  operations["GetCampaignMetricsWithBreakdown"]["parameters"];
type GetAggregatedInternalCampaignMetricsParams =
  operations["GetAggregatedInternalCampaignMetrics"]["parameters"];
type GetInternalCampaignMetricsParams =
  operations["GetInternalCampaignMetrics"]["parameters"];
type InternalCampaignMetricsWithInfo =
  components["schemas"]["InternalCampaignMetricsWithInfo"];

type ApiResponseInternalCampaignMetricsWithInfo =
  components["schemas"]["ApiResponse_InternalCampaignMetricsWithInfo_"];

export const internalCampaignsMetricsApi = createApi({
  reducerPath: "internalCampaignsMetricsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["InternalCampaignMetrics"],
  endpoints: (builder) => ({
    getAllCampaignsMetrics: builder.query<
      EnhancedAllCampaignsResponse,
      GetAllCampaignsMetricsParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params) {
          if (params.startDate)
            queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
        }

        const queryString = queryParams.toString();
        return `/internal-campaign-metrics/all-campaigns${
          queryString ? `?${queryString}` : ""
        }`;
      },
      transformResponse: (response: ApiResponseEnhancedAllCampaignsResponse) =>
        response.data as EnhancedAllCampaignsResponse,
      providesTags: ["InternalCampaignMetrics"],
    }),

    // Get campaign metrics with breakdown by internal campaigns
    getCampaignMetricsWithBreakdown: builder.query<
      CampaignMetricsBreakdown,
      GetCampaignMetricsWithBreakdownParams
    >({
      query: (params) => {
        // Extract path parameters
        const campaignId = params.path.campaignId;
        let queryString = `/internal-campaign-metrics/by-campaign/${campaignId}/breakdown`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponseCampaignMetricsBreakdown) =>
        response.data as CampaignMetricsBreakdown,
      providesTags: ["InternalCampaignMetrics"],
    }),

    // Get aggregated metrics for a specific internal campaign
    getAggregatedInternalCampaignMetrics: builder.query<
      AggregatedMetricsWithSummary,
      GetAggregatedInternalCampaignMetricsParams
    >({
      query: (params) => {
        // Extract path parameters
        const internalCampaignId = params.path.internalCampaignId;
        let queryString = `/internal-campaign-metrics/${internalCampaignId}/aggregated`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponseAggregatedMetricsWithSummary) =>
        response.data as AggregatedMetricsWithSummary,
      providesTags: ["InternalCampaignMetrics"],
    }),

    // Get metrics for a specific internal campaign
    getInternalCampaignMetrics: builder.query<
      components["schemas"]["InternalCampaignMetricsWithInfo"],
      GetInternalCampaignMetricsParams
    >({
      query: (params) => {
        // Extract path parameters
        const internalCampaignId = params.path.internalCampaignId;
        let queryString = `/internal-campaign-metrics/${internalCampaignId}`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (
        response: ApiResponseInternalCampaignMetricsWithInfo
      ) => response.data as InternalCampaignMetricsWithInfo,
      providesTags: ["InternalCampaignMetrics"],
    }),
  }),
});

export const {
  useGetAllCampaignsMetricsQuery,
  useGetCampaignMetricsWithBreakdownQuery,
  useGetAggregatedInternalCampaignMetricsQuery,
  useGetInternalCampaignMetricsQuery,
} = internalCampaignsMetricsApi;
